// pages/Home.jsx
import React, { useState, useEffect } from 'react';
import CartModal from '../components/CartModal';
import Header from '../components/Header';
import Hero from '../components/Hero';
import CategoryTabs from '../components/CategoryTabs';
import MenuList from '../components/MenuList';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorMessage from '../components/ErrorMessage';
import ItemModal from '../components/ItemModal';
import FeedbackModal from '../components/FeedbackModal';
import Footer from '../components/Footer';

const Home = () => {
    const [items, setItems] = useState([]);
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedItem, setSelectedItem] = useState(null);

    // Cart state
    const [cartItems, setCartItems] = useState([]);
    const [isCartOpen, setIsCartOpen] = useState(false);
    const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);

    const handleAddToCart = (item, quantity) => {
        setCartItems(prev => {
            const existingItemIndex = prev.findIndex(i => i.id === item.id);
            if (existingItemIndex > -1) {
                const newCart = [...prev];
                newCart[existingItemIndex].quantity += quantity;
                return newCart;
            }
            return [...prev, { ...item, quantity }];
        });
        setSelectedItem(null); // Close modal after adding
    };

    const handleRemoveFromCart = (index) => {
        setCartItems(prev => prev.filter((_, i) => i !== index));
    };

    // Fetch items from API
    useEffect(() => {
        const fetchItems = async () => {
            try {
                setLoading(true);
                setError(null);

                const response = await fetch('https://at.addistaimrestaurant.com/api/items/');

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const data = await response.json();
                setItems(data);

                // Extract unique categories from items
                const uniqueCategories = ['all', ...new Set(data.map(item => item.category).filter(Boolean))];
                setCategories(uniqueCategories);

            } catch (err) {
                console.error('Fetch error:', err);
                setError('Failed to load menu items. Please check your connection and try again.');
            } finally {
                setLoading(false);
            }
        };

        fetchItems();
    }, []);

    // Filter items based on selected category
    const filteredItems = selectedCategory === 'all'
        ? items
        : items.filter(item => item.category === selectedCategory);

    const handleCategorySelect = (category) => {
        setSelectedCategory(category);
    };

    return (
        <div
            className="min-h-screen bg-fixed bg-cover bg-center"
            style={{ backgroundImage: 'url("https://images.unsplash.com/photo-1528605248644-14dd04022da1?q=80&w=2070&auto=format&fit=crop")' }}
        >
            <div className="min-h-screen bg-black/40 backdrop-blur-sm">
                <div className="max-w-3xl mx-auto bg-gray-50/95 shadow-2xl min-h-screen flex flex-col relative bg-white/20">
                    <Header
                        restaurantName="Addis Taim"
                        cartItemsCount={cartItems.reduce((acc, item) => acc + item.quantity, 0)}
                        onCartClick={() => setIsCartOpen(true)}
                    />

                    <Hero />
                    <CategoryTabs
                        categories={categories}
                        selectedCategory={selectedCategory}
                        onSelectCategory={handleCategorySelect}
                    />

                    <main className="px-4 pb-20 pt-4 flex-grow z-0">
                        {loading && <LoadingSpinner />}

                        {error && <ErrorMessage message={error} />}

                        {!loading && !error && (
                            <MenuList items={filteredItems} onItemClick={(item) => setSelectedItem(item)} />
                        )}
                    </main>

                    <Footer onFeedbackClick={() => setIsFeedbackModalOpen(true)} />
                </div>
            </div>

            {/* MODALS: Placed at the document root to avoid parent constraints */}
            <ItemModal
                item={selectedItem}
                onClose={() => setSelectedItem(null)}
                onAddToCart={handleAddToCart}
            />

            <CartModal
                isOpen={isCartOpen}
                onClose={() => setIsCartOpen(false)}
                cartItems={cartItems}
                onRemoveItem={handleRemoveFromCart}
            />

            <FeedbackModal
                isOpen={isFeedbackModalOpen}
                onClose={() => setIsFeedbackModalOpen(false)}
            />
        </div>
    );
};

export default Home;